import { useEffect, useRef } from "react";
import Intercom, { shutdown, update } from "@intercom/messenger-js-sdk";
import { useViewer } from "~/providers/viewer";
import { Config } from "~/constants/configuration_provider";
import { useGetIntercomTokenQuery } from "~/operations";
import { enqueueSnackbar } from "notistack";
import { HelpOutline } from "@mui/icons-material";
import {
  alpha,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useAppNavDrawer } from "~/providers/app-nav-drawer";

const isChatSupportEnabled = Config.VITE_DISABLE_CHAT_SUPPORT !== "true";

export function IntercomListItemButton() {
  const { viewer } = useViewer();
  const navDrawer = useAppNavDrawer();
  const isInitialized = useRef<boolean>(false);
  const lastMrn = useRef<string | undefined>(undefined);

  const { data: intercomTokenData, error: intercomErrorData } =
    useGetIntercomTokenQuery({
      skip: !isChatSupportEnabled || !viewer,
    });

  const intercomToken = intercomTokenData?.intercomToken;
  const intercomTokenError = intercomErrorData?.message;

  const viewerMrn = viewer?.mrn;
  const viewerCreatedAt = viewer?.createdAt
    ? Number(viewer.createdAt)
    : undefined;
  const viewerEmail = viewer?.email ?? undefined;
  const viewerName = viewer?.name ?? undefined;

  useEffect(() => {
    const shouldShow =
      isChatSupportEnabled &&
      viewer &&
      intercomToken &&
      Config.VITE_INTERCOM_APP_ID;

    if (!shouldShow) {
      if (isInitialized.current || lastMrn.current !== viewerMrn) {
        shutdown();
        isInitialized.current = false;
        lastMrn.current = undefined;
      }
      return;
    }

    if (intercomTokenError) {
      enqueueSnackbar(intercomTokenError, { variant: "error" });
    }

    const userProps = {
      user_id: viewerMrn,
      name: viewerName,
      email: viewerEmail,
      created_at: viewerCreatedAt,
      // only send web tokens in production
      ...(import.meta.env.DEV ? {} : { intercom_user_jwt: intercomToken }),
    };

    if (!isInitialized.current) {
      Intercom({
        api_base: "https://api-iam.eu.intercom.io",
        region: "eu",
        app_id: Config.VITE_INTERCOM_APP_ID || "",
        session_duration: Number(Config.VITE_SESSION_MAX_DURATION) || 86400000,
        hide_default_launcher: true,
        custom_launcher_selector: "#intercom-launcher", // this links to the chat support button component below
        ...userProps,
      });
      isInitialized.current = true;
      lastMrn.current = viewerMrn;
    } else {
      update(userProps);
    }
  }, [
    viewerMrn,
    viewerName,
    viewerEmail,
    viewerCreatedAt,
    intercomToken,
    intercomTokenError,
  ]);

  return (
    <ListItemButton
      id="intercom-launcher"
      href={isChatSupportEnabled ? "#" : "https://mondoo.com/contact"}
      target={isChatSupportEnabled ? undefined : "_blank"}
      sx={[
        {
          px: 1,
          py: 0.5,
          borderRadius: 1,
          color: "text.secondary",
        },
        {
          "&.Mui-selected, &.Mui-selected:hover": {
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.5),
            color: "text.primary",
          },
        },
      ]}
    >
      <ListItemIcon
        sx={{
          minWidth: "24px",
          justifyContent: "center",
          color: "text.secondary",
        }}
      >
        <HelpOutline fontSize="inherit" />
      </ListItemIcon>
      <ListItemText
        primary="Get Support"
        slotProps={{
          primary: {
            sx: {
              opacity: navDrawer.open ? 1 : 0,
              transition: (theme) =>
                theme.transitions.create("opacity", navDrawer.transition),
              ml: 1,
            },
          },
        }}
      />
    </ListItemButton>
  );
}
